import createCache from '@emotion/cache';
import {
  CacheProvider,
  ThemeProvider as StyledThemeProvider
} from '@emotion/react';
import { ThemeProvider } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/styled-engine-sc';
import BranchConnectionVerification from 'BranchConnectionVerification';
import ArchiveAccountErrorAlert from 'framework/base/components/ArchiveAccountErrorAlert';
import { AryAlertTypes } from 'framework/base/components/AryAlert';
import PreventZoomModal from 'framework/base/components/PreventZoomModal';
import { TutorialProvider } from 'framework/base/components/tutorial/TutorialContextProvider';
import {
  GET_BRANCHES,
  GET_CUSTOMERS,
  GET_CUSTOMER_ACCOUNTS,
  GET_DESCRIPTION_TEMPLATES,
  GET_SYSTEM_SETTINGS
} from 'framework/base/constants/aryGraphqlSchemas';
import arySystemSettings, {
  fontSizes
} from 'framework/base/constants/arySystemSettings';
import AdvancedPanelContext from 'framework/base/contexts/AdvancePanelContext';
import DescriptionTemplatesContext from 'framework/base/contexts/DescriptionTemplatesContext';
import LayoutTypeContext from 'framework/base/contexts/LayoutTypeContext';
import ShowCurrenciesContextProvider from 'framework/base/contexts/ShowCurrenciesContextProvider';
import SystemSettingsContext from 'framework/base/contexts/SystemSettingsContext';
import useAryAlert from 'framework/base/hooks/useAryAlert';
import { useAryLazyQuery } from 'framework/base/hooks/useAryGraphql';
import useAryLayoutType from 'framework/base/hooks/useAryLayoutType';
import useAryUsername from 'framework/base/hooks/useAryUsername';
import usePreventZoom from 'framework/base/hooks/usePreventZoom';
import useTemplateParsing from 'framework/base/hooks/useTemplateParsing';
import THEMES from 'framework/base/theme/constants';
import { getFirstDashboardItem } from 'framework/base/utils/Utils';
import AryLoadingAnimation from 'framework/desktop/AryLoadingAnimation';
import TradeCurrencyModal from 'pages/desktop/common-modals/TradeCurrencyModal';
import SubmitTransferBetweenAccountsModal from 'pages/desktop/transfer-between-accounts/SubmitTransferBetweenAccountsModal';
import React, {
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import {
  connectWebSocket,
  disconnectWebSocket
} from 'redux/actions/websocketActions';
import {
  setDoBootstrap,
  setErrors,
  setNoVersionError,
  setShortcutModalState,
  setShouldRefetchCustomers,
  setWhatsappLoginState,
  setwebSocketData,
  updateUserCurrencies
} from 'redux/slices/systemSlice';
import FirebaseNotificationListener from 'framework/base/contexts/firebase/FirebaseNotificationListener';
import { Toaster } from 'sonner';
import {
  ApiAuthorizationConstants,
  AuthRouteGuard as AuthGuard,
  LoginCallbackHandler,
  authService,
  useAuth
} from 'spec';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import './assets/fontYekanBakh.css';
import createTheme from './framework/base/theme/index';
import './language/translation';
import appRoutes, { settingsRoutes } from './routes';

const PersonRegistrationModal = React.lazy(() =>
  import(
    './pages/desktop/person-registration-newDesign/modal/PersonRegistrationModal'
  )
);
const CashTransactionModal = React.lazy(() =>
  import(
    './pages/desktop/account-information/component/modals/CashTransactionModalNewDesign'
  )
);
const DraftSubmissionModal = React.lazy(() =>
  import('./pages/desktop/draft-submission-redesign/DraftSubmissionModal')
);

const SubmitBankTransactionModal = React.lazy(() =>
  import('./pages/desktop/bank-transaction/SubmitBankTransactionModal')
);
const AryNoVersionModal = lazy(() =>
  import('framework/base/components/AryNoVersionModal')
);

const { ApplicationPaths } = ApiAuthorizationConstants;

const DEFAULT_LANGUAGE = 'fa';
const ONE_HOURE_TO_MILISECONDS = 3600000;

const cacheRtl = createCache({
  key: 'muirtl',
  prepend: true,
  stylisPlugins: [prefixer, rtlPlugin]
});

const DirectionProvider = ({ rtl = false, children }) => {
  if (!rtl) {
    return children;
  }
  return <CacheProvider value={cacheRtl}>{children}</CacheProvider>;
};

function lazyRoutes(routes, layoutType, userName) {
  if (!routes || routes.length === 0) return null;
  return (
    <>
      {routes.map((route) => (
        <Route
          key={route.path}
          path={route.path}
          element={
            <AuthGuard
              anonymousAccess={route.anonymousAccess}
              authKeys={route.authKeys}
              path={route.path}
              component={
                layoutType === 'mobile' && route.mobile
                  ? route.mobile
                  : route.component
              }
            />
          }
        >
          {lazyRoutes(route.routes, layoutType, userName)}
        </Route>
      ))}
    </>
  );
}

const contentTypes = {
  loading: 0,
  settings: 1,
  application: 2
};

const App = () => {
  const auth = useAuth();
  const { t, i18n } = useTranslation();
  const reduxStore = useStore();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const langDirection = t('dir');
  const layoutType = useAryLayoutType();
  const openCashTransactionWithShortkey = useSelector(
    (state) => state?.systemReducer?.shortCutModalsStates?.cashTransaction
  );
  const openBankTransactionWithShortkey = useSelector(
    (state) => state?.systemReducer?.shortCutModalsStates?.bankTransaction
  );
  const openTransferBetweenAccountsShortkey = useSelector(
    (state) =>
      state?.systemReducer?.shortCutModalsStates?.transferBetweenAccounts
  );
  const openIncomingDraftWithShortkey = useSelector(
    (state) => state?.systemReducer?.shortCutModalsStates?.incomingDraft
  );
  const openOutgoingDraftWithShortkey = useSelector(
    (state) => state?.systemReducer?.shortCutModalsStates?.outgoingDraft
  );
  const openTransferBetweenAccountsWithShortkey = useSelector(
    (state) =>
      state?.systemReducer?.shortCutModalsStates?.transferBetweenAccounts
  );
  const openBuyCurrencyWithShortkey = useSelector(
    (state) => state?.systemReducer?.shortCutModalsStates?.buyCurrency
  );
  const openSubmitPersonWithShortkey = useSelector(
    (state) => state?.systemReducer?.shortCutModalsStates?.submitPerson
  );

  const [AryAlert, showAlert] = useAryAlert({
    onClose: () => {
      reduxStore.dispatch(setErrors([]));
    }
  });

  // State for custom archive account error alert
  const [showArchiveAlert, setShowArchiveAlert] = useState(false);
  const [archiveError, setArchiveError] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const intervalId = useRef(null);
  const versionExpireTimeRef = useRef(null);
  document.body.dir = langDirection;
  const userName = useAryUsername();

  // Define the WebSocket URL
  const webSocketUrl = 'ws://localhost:5188/ws';

  const hasSubmitDraftAccess = auth?.DraftRE;
  const hasSubmitCashTransactionAccess = auth?.CashTransactionRE;
  const hasSubmitTransferBetweenAccountsAccess =
    auth?.TransferBetweenAccountsRE;
  const hasSubmitBankTransactionAccess = auth?.BankTransactionRE;
  const hasViewDraftDocumentsListAccess = auth?.DraftDocumentsListA;
  const hasViewBranchesListAccess = auth?.BranchesListA;
  const hasSubmitTradeCurrencyAccess = auth?.TradeCurrencyRE;
  const hasRegisterCustomerAccess = auth?.customerR;
  const hasAccessToZoomModal = auth?.SystemSettingsItemA;

  const [contentStatus, setContentStatus] = useState(contentTypes.loading);
  const [systemSettings, setSystemSettings] = useState({});
  const [openAdvancePanelModal, setOpenAdvancePanelModal] = useState(false);
  const [noVersionModalOpen, setNoVersionModalOpen] = useState(false);
  const [advancePanelbuttonRect, setAdvancePanelButtonRect] = useState(null);
  const advancePanelButtonRef = useRef(null);

  const [modalInfo, setModalInfo] = useState({ show: false, x: 0, y: 0 });

  const showPreventZoomModal = ({ type, x, y }) => {
    if (!hasAccessToZoomModal) return;
    if (type === 'scroll') {
      setModalInfo({ show: true, x: x || 0, y: y || 0 }); // Ensure x, y coordinates
    } else if (type === 'keyboard') {
      setModalInfo({
        show: true,
        x: window.innerWidth / 2, // Center modal for keyboard action
        y: window.innerHeight / 2
      });
    }
  };

  const closeModal = () => {
    setModalInfo({ show: false, x: 0, y: 0 });
  };

  usePreventZoom(showPreventZoomModal);

  const handleOpenAdvanceModal = () => {
    if (advancePanelButtonRef.current) {
      const rect = advancePanelButtonRef.current.getBoundingClientRect();
      setAdvancePanelButtonRect(rect);
    }
    setOpenAdvancePanelModal(true);
  };
  const handleAdvancePanelModalClose = () => setOpenAdvancePanelModal(false);
  const [getCustomers, { loading: getCustomersLoading }] = useAryLazyQuery({
    gqlConfig: GET_CUSTOMERS,
    items: `
      customers {
        customerId
        isCompany
      }
      `
  });
  const [getBranches, { loading: loadingGetBranches }] = useAryLazyQuery({
    gqlConfig: GET_BRANCHES,
    items: `
      branches {
        branchId
      }
    `
  });
  const [getCustomerAccounts] = useAryLazyQuery({
    gqlConfig: GET_CUSTOMER_ACCOUNTS,
    items: `
            accountNumber
            `
  });
  const [
    getSystemSettings,
    { data: getSystemSettingsQueryData, loading: getSystemSettingsLoading }
  ] = useAryLazyQuery({
    gqlConfig: GET_SYSTEM_SETTINGS,
    items: `
      systemLanguage
      mainCurrency
      isGregorian
      defaultPhonePrefix
      profitAndLossAccuracy
      documents {
        documentTitle
        isManual
        issuingOrganization
      }
      userCurrencies {
        currencyCode
        decimalPlaces
        dollarRate
        isDivision
      }
      fileId
      exchangeName
      address
      slogan
      descriptions
      groupNames
      phoneNumbers {
        countryCode
        phoneNumber
        communicationWays
      }
      bankInfos {
        accountOwnerName
        bankAccountNumber
        cardNumber
        iBan
      }
      fontSize
      isDarkMode
      timeZone
      showCurrencyByTitle
      isDigitsLatin
      showReportOnWeb
      defaultSendWhatsApp
      defaultUpdateCreditLimit
      cashAccountId
      isTelegramEnabled
      isWhatsappEnabled
      `
  });

  const [getDescriptionTemplates, { data: descriptionTemplates }] =
    useAryLazyQuery({
      gqlConfig: GET_DESCRIPTION_TEMPLATES,
      options: {
        fetchPolicy: 'cache-and-network'
      },
      items: `
        id
        body
      `
    });

  const appTheme = createTheme(
    systemSettings.isDarkMode ? THEMES.DARK : THEMES.DESIGNER,
    systemSettings,
    langDirection,
    layoutType
  );

  async function bootstrap() {
    const resp = await getSystemSettings();
    await getDescriptionTemplates(
      resp?.data?.systemLanguage || DEFAULT_LANGUAGE
    );
    await setFontSize(resp?.data);
    if (!intervalId.current) {
      authService.checkSession();
      intervalId.current = setInterval(() => {
        authService.checkSession();
      }, ONE_HOURE_TO_MILISECONDS);
    }
  }

  async function setFontSize(data) {
    const htmlTag = document.getElementsByTagName('html')[0];
    switch (data?.fontSize) {
      case 1: // small
        htmlTag.style.fontSize = fontSizeCalculation(0.8);
        break;
      case 2: // medium
        htmlTag.style.fontSize = fontSizeCalculation(1);
        break;
      case 3: // large
        htmlTag.style.fontSize = fontSizeCalculation(1.2);
        break;
      default:
        htmlTag.style.fontSize = '16px';
    }
  }
  const fontSizeCalculation = (coefficient) => {
    const minFontSize = 13;
    const maxFontSize = 36;

    // Define the base dimensions for the calculation
    const baseWidth = 1440; // base width for 16px font size
    const baseHeight = 1024; // base height for 16px font size
    const baseFontSize = 16; // base font size for the base dimensions

    // Get the current viewport dimensions
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // make width less important
    const ratio = layoutType === 'laptop' ? 0.9 : 0.5;

    // Use the geometric mean of the scale factors
    const scaleFactor =
      Math.sqrt((vw * ratio) ** 2 + vh ** 2) /
      Math.sqrt((baseWidth * ratio) ** 2 + baseHeight ** 2);

    // Calculate the new font size based on the scale factor and coefficient
    let newFontSize = baseFontSize * scaleFactor * coefficient;

    newFontSize = Math.max(minFontSize, Math.min(newFontSize, maxFontSize));

    // Round the final font size to top
    return `${Math.floor(newFontSize)}px`;
  };

  const parseDescription = useTemplateParsing(descriptionTemplates);

  function hasSystemSettings(settings) {
    if (!settings) return false;
    if (
      !settings.mainCurrency ||
      !settings.profitAndLossAccuracy ||
      !settings.userCurrencies?.length ||
      !settings.exchangeName ||
      !settings.groupNames?.length
    ) {
      return false;
    }
    return true;
  }

  function getBaseRoute() {
    switch (contentStatus) {
      case contentTypes.loading:
        return <Route path="/*" element={<AryLoadingAnimation />} />;
      case contentTypes.settings:
        return (
          <>
            <Route path="/*" element={<Navigate replace to="/welcome" />} />
            {lazyRoutes(settingsRoutes, layoutType, userName)}
          </>
        );
      case contentTypes.application: {
        if (!userName || !descriptionTemplates?.length || !auth) {
          return <Route path="/*" element={<AryLoadingAnimation />} />;
        }
        const firstItem = getFirstDashboardItem(auth?.role, auth?.appType);
        let defaultRedirectPath = '/dashboard/bi-dashboard';

        if (firstItem && firstItem.href) {
          defaultRedirectPath = firstItem.href.startsWith('./')
            ? firstItem.href.replace(/^\.\//, '/dashboard/')
            : firstItem.href;
        }

        return (
          <>
            <Route
              path="/system-settings"
              element={<Navigate replace to={defaultRedirectPath} />}
            />
            <Route
              path="/"
              element={<Navigate replace to={defaultRedirectPath} />}
            />
            {lazyRoutes(appRoutes, layoutType, userName)}
          </>
        );
      }
      default:
        return null;
    }
  }

  reduxStore.subscribe(() => {
    const { errors } = reduxStore.getState().systemReducer;
    if (errors.length) {
      if (
        errors[0].templateId === 'Branch-EX-Branch_Cant_Archive' ||
        errors[0].templateId === 'BANK-Ex-CANT_ARCHIVE_BALANCE_MUST_BE_ZERO'
      ) {
        setArchiveError(
          parseDescription(errors[0].templateId, errors[0].metadata)
        );
        setErrorType(errors[0].templateId);
        setShowArchiveAlert(true);
      } else {
        showAlert(
          AryAlertTypes.ERROR,
          parseDescription(errors[0].templateId, errors[0].metadata)
        );
      }
    }
  });

  useEffect(() => {
    if (window.location.pathname !== ApplicationPaths.LoginCallback) {
      bootstrap();
    }

    reduxStore.subscribe(() => {
      const { noVersionError, doBootstrap } =
        reduxStore.getState().systemReducer;
      if (noVersionError) {
        setNoVersionModalOpen(true);
        reduxStore.dispatch(setNoVersionError(false));
      }
      if (doBootstrap) {
        reduxStore.dispatch(setDoBootstrap(false));
        bootstrap();
      }
    });
  }, []);

  useEffect(() => {
    (async () => {
      if (!getSystemSettingsLoading && getSystemSettingsQueryData) {
        if (hasSystemSettings(getSystemSettingsQueryData)) {
          i18n.changeLanguage(
            getSystemSettingsQueryData[arySystemSettings.systemLanguage] || 'fa'
          );
          setSystemSettings(getSystemSettingsQueryData);
          if (getSystemSettingsQueryData.isTelegramEnabled) {
            dispatch(setWhatsappLoginState(true));
          }
          setContentStatus(contentTypes.application);
          window.paths.rptf =
            layoutType === 'mobile'
              ? window.paths.rptw
              : getSystemSettingsQueryData[arySystemSettings.showReportOnWeb]
              ? window.paths.rptw
              : window.paths.rptd;
          reduxStore.dispatch(updateUserCurrencies(true));
          const isVersionValid = await authService.isVersionValid();
          if (!isVersionValid) {
            if (
              window.location.pathname !==
              `/${ApplicationPaths.BranchConnection}`
            ) {
              reduxStore.dispatch(setNoVersionError(true));
            }
          }
        } else {
          const versionExpireTime = await authService.getVersionExpireTime();
          versionExpireTimeRef.current = versionExpireTime;
          if (new Date().getTime() < +versionExpireTime) {
            if (
              window.location.pathname ===
              `/${ApplicationPaths.BranchConnection}`
            ) {
              setContentStatus(contentTypes.application);
            } else {
              setContentStatus(contentTypes.settings);
            }
          } else if (
            window.location.pathname === `/${ApplicationPaths.BranchConnection}`
          ) {
            setContentStatus(contentTypes.application);
          } else {
            reduxStore.dispatch(setNoVersionError(true));
          }
        }
      }
    })();
  }, [getSystemSettingsQueryData]);

  useEffect(
    () => () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    },
    []
  );

  const navigateToFirstAccount = async () => {
    const result = await getCustomers({
      pagination: {
        pageIndex: 0,
        pageCount: 1
      },
      groupCodes: [],
      customerType: GET_CUSTOMERS.customerType.ALL,
      searchText: '',
      hasAccount: true,
      searchType: 'ALL'
    });

    const customers = result?.data?.customers;
    if (customers?.length > 0) {
      const row = customers[0];
      const resp = await getCustomerAccounts(row.customerId);
      navigate('/dashboard/global-ledger/customer/account-information', {
        state: {
          accountNumber: resp.data[0].accountNumber,
          personId: row.customerId,
          personType: +!row.isCompany,
          showFirstAccount: true
        }
      });
    }
  };
  const navigateToFirstBranch = async () => {
    const result = await getBranches({
      connectionType: 'ALL',
      pagination: { pageIndex: 0, pageCount: 1 },
      searchText: ''
    });
    const { branchId } = result.data.branches[0];

    navigate('/dashboard/global-ledger/branch/account-information', {
      state: {
        branchId,
        autoFocus: true
      }
    });
  };

  const closeAllShortcutModals = (exceptModalName) => {
    const modalNames = [
      'outgoingDraft',
      'incomingDraft',
      'cashTransaction',
      'bankTransaction',
      'transferBetweenAccounts',
      'buyCurrency',
      'submitPerson'
    ];

    modalNames.forEach((modalName) => {
      if (!exceptModalName || modalName !== exceptModalName) {
        dispatch(
          setShortcutModalState({
            modalName,
            value: false
          })
        );
      }
    });
  };

  useEffect(() => {
    if (userName && window.gtag) {
      window.gtag('config', 'G-7QMF1E39JZ', {
        user_properties: {
          username: userName
        }
      });
    }
  }, [userName]);

  useEffect(() => {
    if (userName && window.clarity) {
      window.clarity('identify', userName);

      window.clarity('set', 'custom_user_id', userName);
    }
  }, [userName]);

  useEffect(() => {
    if (!userName) return;
    if (contentStatus === contentTypes.application) {
      document.addEventListener('keydown', async (event) => {
        if (event.ctrlKey && event.key === 'F5') {
          event.stopImmediatePropagation();
          event.stopPropagation();
          event.preventDefault();
        }
        if (event.ctrlKey && event.shiftKey) {
          switch (event.code) {
            case 'Backquote':
              if (hasRegisterCustomerAccess) {
                closeAllShortcutModals('submitPerson');
                dispatch(setwebSocketData(null));
                dispatch(
                  setShortcutModalState({
                    modalName: 'submitPerson',
                    value: true
                  })
                );
              }
              break;
            case 'Digit1': {
              if (hasViewDraftDocumentsListAccess) {
                closeAllShortcutModals();
                navigate('/dashboard/draft/orders/list');
              }
              break;
            }
            case 'Digit2': {
              closeAllShortcutModals();
              await navigateToFirstAccount();
              break;
            }
            case 'Digit3': {
              if (hasViewBranchesListAccess) {
                closeAllShortcutModals();
                await navigateToFirstBranch();
              }
              break;
            }
            case 'Digit4': {
              if (hasSubmitDraftAccess) {
                event.preventDefault();
                closeAllShortcutModals('outgoingDraft');
                dispatch(
                  setShortcutModalState({
                    modalName: 'outgoingDraft',
                    value: true
                  })
                );
              }
              break;
            }
            case 'Digit5': {
              if (hasSubmitDraftAccess) {
                event.preventDefault();
                closeAllShortcutModals('incomingDraft');
                dispatch(
                  setShortcutModalState({
                    modalName: 'incomingDraft',
                    value: true
                  })
                );
              }
              break;
            }
            case 'Digit6': {
              if (hasSubmitCashTransactionAccess) {
                closeAllShortcutModals('cashTransaction');
                dispatch(
                  setShortcutModalState({
                    modalName: 'cashTransaction',
                    value: true
                  })
                );
              }
              break;
            }
            case 'Digit7': {
              if (hasSubmitBankTransactionAccess) {
                closeAllShortcutModals('bankTransaction');
                dispatch(
                  setShortcutModalState({
                    modalName: 'bankTransaction',
                    value: true
                  })
                );
              }
              break;
            }
            case 'Digit8': {
              if (hasSubmitTransferBetweenAccountsAccess) {
                closeAllShortcutModals('transferBetweenAccounts');
                dispatch(
                  setShortcutModalState({
                    modalName: 'transferBetweenAccounts',
                    value: true
                  })
                );
              }
              break;
            }
            case 'Digit9': {
              if (hasSubmitTradeCurrencyAccess) {
                closeAllShortcutModals('buyCurrency');
                dispatch(
                  setShortcutModalState({
                    modalName: 'buyCurrency',
                    value: true
                  })
                );
              }
              break;
            }
            default:
              break;
          }
        }
        if (event.ctrlKey && event.shiftKey && event.code === 'KeyF') {
          handleOpenAdvanceModal();
        }
      });
    }
  }, [contentStatus, userName]);

  // Initialize WebSocket connection when the component mounts
  useEffect(() => {
    dispatch(connectWebSocket(webSocketUrl));

    return () => {
      dispatch(disconnectWebSocket());
    };
  }, [dispatch]);

  const advancedPanelContextValue = useMemo(
    () => ({
      openAdvancePanelModal,
      handleAdvancePanelModalClose,
      advancePanelbuttonRect,
      handleOpenAdvanceModal,
      advancePanelButtonRef
    }),
    [
      openAdvancePanelModal,
      handleAdvancePanelModalClose,
      advancePanelbuttonRect,
      handleOpenAdvanceModal,
      advancePanelButtonRef
    ]
  );

  const systemSettingsContextValue = useMemo(
    () => ({ systemSettings, setSystemSettings, getSystemSettings }),
    [systemSettings, setSystemSettings, getSystemSettings]
  );

  return (
    <HelmetProvider>
      <Helmet
        titleTemplate={`%s | ${t('Moneyx')}`}
        defaultTitle={`${t('Moneyx')} - ${t('Exchange Accounting Software')}`}
      />
      <SystemSettingsContext.Provider value={systemSettingsContextValue}>
        <AdvancedPanelContext.Provider value={advancedPanelContextValue}>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={appTheme}>
              <StyledThemeProvider theme={appTheme}>
                <DirectionProvider rtl={langDirection === 'rtl'}>
                  <LayoutTypeContext.Provider value={layoutType}>
                    <DescriptionTemplatesContext.Provider
                      value={descriptionTemplates}
                    >
                      <TutorialProvider>
                        <ShowCurrenciesContextProvider>
                          <Routes>
                            {getBaseRoute()}
                            <Route
                              exact
                              path={ApplicationPaths.LoginCallback}
                              element={<LoginCallbackHandler />}
                            />
                            <Route
                              exact
                              path={ApplicationPaths.BranchConnection}
                              element={
                                <BranchConnectionVerification
                                  applicationContentType={
                                    contentTypes.application
                                  }
                                  contentStatus={contentStatus}
                                />
                              }
                            />
                          </Routes>
                          {AryAlert}
                          {/* Custom Archive Account Error Alert */}
                          <ArchiveAccountErrorAlert
                            open={showArchiveAlert}
                            error={archiveError}
                            type={errorType}
                            onClose={() => {
                              setShowArchiveAlert(false);
                              reduxStore.dispatch(setErrors([]));
                            }}
                          />

                          {
                            // CashTransaction
                          }
                          {openCashTransactionWithShortkey && (
                            <Suspense fallback={<AryLoadingAnimation />}>
                              <CashTransactionModal
                                useShortCut
                                open={openCashTransactionWithShortkey}
                                handleClose={() => {
                                  dispatch(
                                    setShortcutModalState({
                                      modalName: 'cashTransaction',
                                      value: false
                                    })
                                  );
                                }}
                              />
                            </Suspense>
                          )}
                          {
                            // BankTransaction
                          }
                          {openBankTransactionWithShortkey && (
                            <Suspense fallback={<AryLoadingAnimation />}>
                              <SubmitBankTransactionModal
                                open={openBankTransactionWithShortkey}
                                handleClose={() => {
                                  dispatch(
                                    setShortcutModalState({
                                      modalName: 'bankTransaction',
                                      value: false
                                    })
                                  );
                                }}
                              />
                            </Suspense>
                          )}
                          {openTransferBetweenAccountsShortkey && (
                            <Suspense fallback={<AryLoadingAnimation />}>
                              <SubmitTransferBetweenAccountsModal
                                open={openTransferBetweenAccountsShortkey}
                                handleClose={() => {
                                  dispatch(
                                    setShortcutModalState({
                                      modalName: 'transferBetweenAccounts',
                                      value: false
                                    })
                                  );
                                }}
                              />
                            </Suspense>
                          )}
                          {openIncomingDraftWithShortkey && (
                            <Suspense fallback={<AryLoadingAnimation />}>
                              <DraftSubmissionModal
                                draftType="incoming"
                                open={openIncomingDraftWithShortkey}
                                closeParentModal={() => {
                                  dispatch(
                                    setShortcutModalState({
                                      modalName: 'incomingDraft',
                                      value: false
                                    })
                                  );
                                }}
                                handleClose={() => {
                                  dispatch(
                                    setShortcutModalState({
                                      modalName: 'incomingDraft',
                                      value: false
                                    })
                                  );
                                }}
                              />
                            </Suspense>
                          )}
                          {
                            // Incoming Draft
                          }
                          {(loadingGetBranches || getCustomersLoading) && (
                            <AryLoadingAnimation />
                          )}
                          {
                            // Outgoing Draft
                          }
                          {openOutgoingDraftWithShortkey && (
                            <Suspense fallback={<AryLoadingAnimation />}>
                              <DraftSubmissionModal
                                draftType="outgoing"
                                open={openOutgoingDraftWithShortkey}
                                closeParentModal={() => {
                                  dispatch(
                                    setShortcutModalState({
                                      modalName: 'outgoingDraft',
                                      value: false
                                    })
                                  );
                                }}
                                handleClose={() => {
                                  dispatch(
                                    setShortcutModalState({
                                      modalName: 'outgoingDraft',
                                      value: false
                                    })
                                  );
                                }}
                              />
                            </Suspense>
                          )}
                          {
                            // Trade Currency
                          }
                          <Suspense fallback={<AryLoadingAnimation />}>
                            <TradeCurrencyModal
                              open={openBuyCurrencyWithShortkey}
                              handleClose={() => {
                                dispatch(
                                  setShortcutModalState({
                                    modalName: 'buyCurrency',
                                    value: false
                                  })
                                );
                              }}
                              tradeType="buy"
                            />
                          </Suspense>

                          {
                            // Transfer Between Accounts
                          }
                          {openTransferBetweenAccountsWithShortkey && (
                            <Suspense fallback={<AryLoadingAnimation />}>
                              <SubmitTransferBetweenAccountsModal
                                open={openTransferBetweenAccountsWithShortkey}
                                handleClose={() => {
                                  dispatch(
                                    setShortcutModalState({
                                      modalName: 'transferBetweenAccounts',
                                      value: false
                                    })
                                  );
                                }}
                              />
                            </Suspense>
                          )}
                          {openSubmitPersonWithShortkey && (
                            <Suspense fallback={<AryLoadingAnimation />}>
                              <PersonRegistrationModal
                                title={t('AddPerson')}
                                onClose={() => {
                                  dispatch(setShouldRefetchCustomers(true));
                                  dispatch(
                                    setShortcutModalState({
                                      modalName: 'submitPerson',
                                      value: false
                                    })
                                  );
                                }}
                                open={openSubmitPersonWithShortkey}
                              />
                            </Suspense>
                          )}
                          {noVersionModalOpen && (
                            <Suspense fallback="">
                              <AryNoVersionModal
                                open={noVersionModalOpen}
                                handleClose={() => setNoVersionModalOpen(false)}
                                showConnectedBranchesButton
                                handleOnConnectedBranchesButtonClicked={() => {
                                  if (!versionExpireTimeRef.current) {
                                    setSystemSettings({
                                      mainCurrency: 'USD',
                                      fontSize: fontSizes.medium,
                                      isDigitsLatin: false,
                                      defaultPhonePrefix: '98',
                                      timeZone: 'Asia/Tehran',
                                      isGregorian: false,
                                      showCurrencyByTitle: true,
                                      profitAndLossAccuracy: 15,
                                      documents: [
                                        {
                                          documentTitle: '',
                                          isManual: false,
                                          issuingOrganization: ''
                                        }
                                      ],
                                      isDarkMode: false,
                                      file: undefined,
                                      exchangeName: '',
                                      phoneNumbers: [
                                        {
                                          phoneNumber: '',
                                          countryCode: '98',
                                          communicationWays: [1]
                                        }
                                      ],
                                      address: '',
                                      slogan: '',
                                      descriptions: [''],
                                      groupNames: [
                                        t('General'),
                                        t('Banking'),
                                        t('Cost')
                                      ],
                                      userCurrencies: [
                                        {
                                          currencyCode: 'USD',
                                          decimalPlaces: 2,
                                          dollarRate: 1,
                                          isDivision: false
                                        }
                                      ],
                                      showReportOnWeb: true
                                    });
                                  }
                                  setContentStatus(contentTypes.application);
                                }}
                              />
                            </Suspense>
                          )}
                          {showPreventZoomModal && (
                            <Suspense fallback="">
                              <PreventZoomModal
                                x={modalInfo.x}
                                y={modalInfo.y}
                                open={modalInfo.show}
                                onClose={closeModal}
                                data={getSystemSettingsQueryData}
                              />
                            </Suspense>
                          )}
                        </ShowCurrenciesContextProvider>
                      </TutorialProvider>
                    </DescriptionTemplatesContext.Provider>
                    <Toaster
                      position={
                        layoutType === 'mobile' ? 'top-right' : 'bottom-right'
                      }
                    />
                    <FirebaseNotificationListener />
                  </LayoutTypeContext.Provider>
                </DirectionProvider>
              </StyledThemeProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </AdvancedPanelContext.Provider>
      </SystemSettingsContext.Provider>
    </HelmetProvider>
  );
};

export default App;
